import { z } from 'zod';
import { FORM_FIELD_KEY_PATTERN } from './form-schema.js';

export const PRINTOUT_TEMPLATE_PREVIEW_REFERENCE_CODE = 'BT7K2M9QXQ';

export const PRINTOUT_BUILT_IN_BLOCKS = [
  'logo',
  'text',
  'qrCode',
  'donationCode',
  'campaign',
  'season',
  'payer',
  'payerPhone',
  'owner',
  'reference',
  'yurt',
  'amount',
  'quantity',
  'donatedAt',
  'queuePosition',
] as const;

export type PrintoutBuiltInBlock = (typeof PRINTOUT_BUILT_IN_BLOCKS)[number];

export const PRINTOUT_REQUIRED_BLOCKS = [
  'donationCode',
] as const satisfies readonly PrintoutBuiltInBlock[];

export const PRINTOUT_BLOCK_SIZES = ['small', 'normal', 'large'] as const;

export type PrintoutBlockSize = (typeof PRINTOUT_BLOCK_SIZES)[number];

export const PRINTOUT_BLOCK_LABEL_MAX_LENGTH = 120;
export const PRINTOUT_TEMPLATE_NAME_MAX_LENGTH = 120;
export const PRINTOUT_TEMPLATE_MAX_BLOCKS = 40;

const labelSchema = z.string().max(PRINTOUT_BLOCK_LABEL_MAX_LENGTH).nullable();

const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const printoutTypographySchema = z.object({
  fontFamily: z.enum(['sans', 'mono']).optional(),
  fontSize: z.number().min(6).max(96).optional(),
  fontWeight: z.enum(['regular', 'medium', 'bold']).optional(),
  lineHeight: z.number().min(1).max(3).optional(),
  color: colorSchema.optional(),
  backgroundColor: colorSchema.optional(),
  align: z.enum(['left', 'center', 'right', 'justify']).optional(),
});
export const printoutBlockStyleSchema = printoutTypographySchema.extend({
  padding: z.number().min(0).max(100).optional(),
  margin: z.number().min(0).max(100).optional(),
  spacingUnit: z.enum(['pt', '%']).optional(),
  columnSpan: z.number().int().min(1).max(4).optional(),
  x: z.number().min(0).max(2500).optional(),
  y: z.number().min(0).max(2500).optional(),
  width: z.number().min(20).max(2500).optional(),
  height: z.number().min(20).max(2500).optional(),
  zIndex: z.number().int().min(-100).max(100).optional(),
});
const blockOptions = {
  hidden: z.boolean().optional(),
  text: z.string().max(4000).optional(),
  attachmentId: z.uuid().optional(),
  keepAspectRatio: z.boolean().optional(),
  qrSize: z.number().min(60).max(300).optional(),
  qrEcc: z.enum(['L', 'M', 'Q', 'H']).optional(),
  labelVisible: z.boolean().optional(),
  labelPosition: z.enum(['above', 'inline']).optional(),
  style: printoutBlockStyleSchema.optional(),
  labelStyle: printoutTypographySchema.optional(),
};
export const printoutPageSettingsSchema = z.object({
  size: z.enum(['A4', 'A5', 'thermal', 'custom']).default('A4'),
  orientation: z.enum(['portrait', 'landscape']).default('portrait'),
  widthMm: z.number().min(58).max(500).default(210),
  heightMm: z.number().min(58).max(1000).default(297),
  margins: z
    .object({
      top: z.number().min(0).max(80),
      right: z.number().min(0).max(80),
      bottom: z.number().min(0).max(80),
      left: z.number().min(0).max(80),
    })
    .default({ top: 14.82, right: 14.82, bottom: 14.82, left: 14.82 }),
  backgroundColor: colorSchema.default('#FFFFFF'),
  columns: z.number().int().min(1).max(4).default(1),
  gap: z.number().min(0).max(100).default(8),
  watermarkAttachmentId: z.uuid().optional(),
  watermarkOpacity: z.number().min(0).max(1).default(0.12),
  showHeader: z.boolean().default(true),
  showSignature: z.boolean().default(true),
});
export type PrintoutPageSettings = z.infer<typeof printoutPageSettingsSchema>;
export type PrintoutBlockStyle = z.infer<typeof printoutBlockStyleSchema>;
export function initialPrintoutPageSettings(): PrintoutPageSettings {
  return printoutPageSettingsSchema.parse({});
}
export function printoutPageDimensions(settings: PrintoutPageSettings): {
  width: number;
  height: number;
} {
  const dimensions =
    settings.size === 'A4'
      ? [210, 297]
      : settings.size === 'A5'
        ? [148, 210]
        : settings.size === 'thermal'
          ? [80, settings.heightMm]
          : [settings.widthMm, settings.heightMm];
  const [width = 210, height = 297] =
    settings.orientation === 'landscape' ? [dimensions[1], dimensions[0]] : dimensions;
  return { width, height };
}

export const printoutBuiltInBlockSchema = z.object({
  ...blockOptions,
  kind: z.enum(PRINTOUT_BUILT_IN_BLOCKS),
  label: labelSchema,
  size: z.enum(PRINTOUT_BLOCK_SIZES),
});

export const printoutFieldBlockSchema = z.object({
  ...blockOptions,
  kind: z.literal('field'),
  fieldKey: z.string().regex(FORM_FIELD_KEY_PATTERN),
  label: labelSchema,
  size: z.enum(PRINTOUT_BLOCK_SIZES),
});

export const printoutTemplateBlockSchema = z.union([
  printoutBuiltInBlockSchema,
  printoutFieldBlockSchema,
]);

export type PrintoutBuiltInBlockEntry = z.infer<typeof printoutBuiltInBlockSchema>;
export type PrintoutFieldBlock = z.infer<typeof printoutFieldBlockSchema>;
export type PrintoutTemplateBlock = z.infer<typeof printoutTemplateBlockSchema>;

/** The shape a client sends and a read returns; `printoutTemplateProblem` judges the list. */
export const printoutTemplateBlocksSchema = z
  .array(printoutTemplateBlockSchema)
  .max(PRINTOUT_TEMPLATE_MAX_BLOCKS);

export const PRINTOUT_TEMPLATE_PROBLEMS = [
  'PRINTOUT_TEMPLATE_REQUIRED_BLOCK_MISSING',
  'PRINTOUT_TEMPLATE_BLOCK_REPEATED',
  'PRINTOUT_TEMPLATE_BLOCK_TOO_SMALL',
  'PRINTOUT_TEMPLATE_OCR_UNSAFE',
  'PRINTOUT_TEMPLATE_LAYOUT_INVALID',
] as const;

export type PrintoutTemplateProblem = (typeof PRINTOUT_TEMPLATE_PROBLEMS)[number];

export function isFieldBlock(block: PrintoutTemplateBlock): block is PrintoutFieldBlock {
  return block.kind === 'field';
}

/** A block's identity within one template: a built-in once, a field key once. */
export function printoutBlockIdentity(block: PrintoutTemplateBlock): string {
  return isFieldBlock(block) ? `field:${block.fieldKey}` : block.kind;
}

export function isRequiredPrintoutBlock(block: PrintoutTemplateBlock): boolean {
  return (PRINTOUT_REQUIRED_BLOCKS as readonly string[]).includes(block.kind);
}

export function printoutTemplateProblem(
  blocks: readonly PrintoutTemplateBlock[],
  settings?: PrintoutPageSettings,
): PrintoutTemplateProblem | null {
  if (!blocks.some((block) => block.kind === 'donationCode' && !block.hidden))
    return 'PRINTOUT_TEMPLATE_REQUIRED_BLOCK_MISSING';
  const identities = blocks.map(printoutBlockIdentity);
  if (new Set(identities).size !== identities.length) return 'PRINTOUT_TEMPLATE_BLOCK_REPEATED';
  if (blocks.some((block) => block.kind === 'donationCode' && block.size === 'small'))
    return 'PRINTOUT_TEMPLATE_BLOCK_TOO_SMALL';
  const page = settings ?? initialPrintoutPageSettings();
  const { width, height } = printoutPageDimensions(page);
  const usableWidth = ((width - page.margins.left - page.margins.right) * 72) / 25.4;
  const usableHeight = ((height - page.margins.top - page.margins.bottom) * 72) / 25.4;
  if (
    usableWidth < 60 ||
    usableHeight < 60 ||
    usableWidth - (page.columns - 1) * page.gap < page.columns * 20
  )
    return 'PRINTOUT_TEMPLATE_LAYOUT_INVALID';
  for (const block of blocks.filter((block) => !block.hidden)) {
    const style = block.style;
    if (
      style &&
      ((style.x === undefined) !== (style.y === undefined) ||
        (style.columnSpan ?? 1) > page.columns ||
        (style.x ?? 0) + (style.width ?? 20) > usableWidth ||
        (style.y ?? 0) + (style.height ?? 20) > usableHeight)
    )
      return 'PRINTOUT_TEMPLATE_LAYOUT_INVALID';
    if (block.kind === 'donationCode') {
      const fontSize = style?.fontSize ?? 24;
      const background = style?.backgroundColor ?? page.backgroundColor;
      if (
        fontSize < 18 ||
        contrastRatio(style?.color ?? '#000000', background) < 7 ||
        (page.watermarkAttachmentId && !style?.backgroundColor)
      )
        return 'PRINTOUT_TEMPLATE_OCR_UNSAFE';
    }
  }
  return null;
}
function luminance(hex: string): number {
  const channels = [1, 3, 5]
    .map((index) => Number.parseInt(hex.slice(index, index + 2), 16) / 255)
    .map((value) => (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));
  return (channels[0] ?? 0) * 0.2126 + (channels[1] ?? 0) * 0.7152 + (channels[2] ?? 0) * 0.0722;
}
export function contrastRatio(left: string, right: string): number {
  const a = luminance(left),
    b = luminance(right);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * Trims every label and turns a blank one into null, so a stored template
 * never carries a label with nothing to show — clearing the field is how the
 * editor returns a block to its default label.
 */
export function normalisePrintoutTemplateBlocks(
  blocks: readonly PrintoutTemplateBlock[],
): PrintoutTemplateBlock[] {
  return blocks.map((block) => {
    const trimmed = block.label?.trim() ?? '';
    return { ...block, label: trimmed.length > 0 ? trimmed : null };
  });
}

/** New templates offer QR by default; only the visible donation code is required. */
export function initialPrintoutTemplateBlocks(): PrintoutTemplateBlock[] {
  return (['qrCode', 'donationCode'] as const).map((kind) => ({
    kind,
    label: null,
    size: 'normal',
  }));
}
